import * as THREE from 'three';
import type { Character, Side } from '../data/types';
import { Unit, makeUnit, getAbilityDef } from './unit';
import { getAbility } from './abilities';
import { SFX } from '../sound';

interface Projectile {
  attacker: Unit;
  pos: THREE.Vector3;
  target: Unit;
  damage: number;
  type: string;
  ignoreDef: boolean;
  group: THREE.Group;
  core: THREE.Sprite;
  glow: THREE.Sprite;
  speed: number;
  alive: boolean;
  trailT: number;
  spinT: number;
}

interface FloatingDamage {
  pos: THREE.Vector3;
  value: number;
  t: number;
  sprite: THREE.Sprite;
}

interface Particle {
  mesh: THREE.Sprite;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  startScale: number;
  gravity: number;
}

const COLORS: Record<string, number> = {
  fire: 0xff6b3d,
  ice: 0x88ddff,
  electric: 0xffee44,
  grass: 0x66ee66,
  shadow: 0x9b59ff,
  physical: 0xffffff,
  heal: 0x4dffa8,
  aoe: 0xff8844,
};

// ---------- procedural textures (so glow sprites are circles, not squares) ----------
let _glowTex: THREE.Texture | null = null;
function glowTexture(): THREE.Texture {
  if (_glowTex) return _glowTex;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0.00, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.85)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.35)');
  g.addColorStop(1.00, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  _glowTex = t;
  return t;
}

let _sparkTex: THREE.Texture | null = null;
function sparkTexture(): THREE.Texture {
  if (_sparkTex) return _sparkTex;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d')!;
  // bright tight core with a soft falloff
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0.0, 'rgba(255,255,255,1)');
  g.addColorStop(0.15, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.25)');
  g.addColorStop(1.0, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  _sparkTex = t;
  return t;
}

// Arena bounds — must stay inside the camera horizontal frustum at z≈0.
// Camera at (0,16,26) with FOV 50, aspect ~2.3 sees roughly ±20 horizontally
// at the play plane, so 18 is a safe inner bound.
const ARENA_X = 18;
const ARENA_Z = 9;

export class GameEngine {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  units: Unit[] = [];
  projectiles: Projectile[] = [];
  particles: Particle[] = [];
  floats: FloatingDamage[] = [];
  raf = 0;
  lastT = 0;
  paused = false;
  startTime = 0;
  onWin?: (winner: Side, duration: number) => void;
  pendingSpell: 'fireball' | 'freeze' | 'heal' | 'lightning' | 'meteor' | 'shield' | 'ultimate' | null = null;
  raycaster = new THREE.Raycaster();
  mouseNDC = new THREE.Vector2();
  // Camera shake
  shakeT = 0;
  shakeMag = 0;
  baseCamPos = new THREE.Vector3(0, 16, 28);
  // Kill streak / slow-mo / ultimate state
  killStreak: Record<Side, { count: number; lastT: number }> = {
    left: { count: 0, lastT: -99 },
    right: { count: 0, lastT: -99 },
  };
  ultMeter: Record<Side, number> = { left: 0, right: 0 };
  ULT_MAX = 1500;
  slowMoT = 0;
  winPending: { winner: Side; dur: number } | null = null;
  winDelay = 0;
  reinforcementsSent = false;
  leftPool: Character[] = [];
  rightPool: Character[] = [];
  // Weather
  weather: 'clear' | 'rain' | 'thunderstorm' | 'sandstorm' = 'clear';
  weatherT = 0;
  rainGroup: THREE.Group | null = null;
  // Hazards
  hazards: { kind: 'lava' | 'heal' | 'speed'; pos: THREE.Vector3; radius: number; mesh: THREE.Mesh }[] = [];
  // New: hit-stop, intro, KO zoom, MVP tracking, modifiers
  hitStopT = 0;
  introDuration = 6.0;
  introT = 6.0;          // intro fly-by duration remaining
  introActive = true;
  // Cinematic camera cuts during fight
  cineT = 0;             // remaining duration of current cinematic shot
  cineCooldown = 5;      // seconds until next possible cinematic
  cineFrom = new THREE.Vector3();
  cineTo = new THREE.Vector3();
  cineLook = new THREE.Vector3();
  cineProgress = 0;
  cineDuration = 0;
  showdownActive = false;
  koZoomTarget: THREE.Vector3 | null = null;
  modifier: 'none' | 'meteor_storm' | 'double_mana' | 'half_hp' | 'lava_floor' | 'sudden_death' = 'none';
  meteorStormT = 0;
  critsLanded = 0;
  ultsCast = 0;
  // damage tracking per unit (kills+dmg) lives on unit objects via _kills/_dmg

  constructor(public canvas: HTMLCanvasElement) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x87ceeb);
    this.scene.fog = new THREE.Fog(0x87ceeb, 30, 80);

    const w0 = canvas.clientWidth || 1280;
    const h0 = canvas.clientHeight || 720;
    this.camera = new THREE.PerspectiveCamera(50, w0 / h0, 0.1, 200);
    this.camera.position.set(0, 16, 28);
    this.camera.lookAt(0, 2, 0);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w0, h0, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.buildArena();
    this.bindEvents();
    window.addEventListener('resize', this.onResize);
  }

  buildArena() {
    // sun
    const sun = new THREE.DirectionalLight(0xfff4d6, 1.6);
    sun.position.set(10, 20, 10);
    this.scene.add(sun);
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const back = new THREE.DirectionalLight(0x8899ff, 0.4);
    back.position.set(-10, 10, -10);
    this.scene.add(back);

    // ground
    const groundGeo = new THREE.PlaneGeometry(60, 30, 1, 1);
    const groundMat = new THREE.MeshLambertMaterial({ color: 0x4a8b3d });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    this.scene.add(ground);

    // grid lines
    const grid = new THREE.GridHelper(60, 30, 0x000000, 0x3a6b2d);
    (grid.material as THREE.Material).opacity = 0.25;
    (grid.material as THREE.Material).transparent = true;
    grid.position.y = 0.01;
    this.scene.add(grid);

    // arena fence
    const fenceMat = new THREE.MeshLambertMaterial({ color: 0x8b6f3a });
    for (let x = -30; x <= 30; x += 3) {
      const post = new THREE.Mesh(new THREE.BoxGeometry(0.3, 1.2, 0.3), fenceMat);
      post.position.set(x, 0.6, -15);
      this.scene.add(post);
      const post2 = post.clone();
      post2.position.z = 15;
      this.scene.add(post2);
    }
    // rails
    const rail1 = new THREE.Mesh(new THREE.BoxGeometry(60, 0.15, 0.15), fenceMat);
    rail1.position.set(0, 1.0, -15);
    this.scene.add(rail1);
    const rail2 = rail1.clone(); rail2.position.z = 15; this.scene.add(rail2);

    // some decorative rocks/bushes
    for (let i = 0; i < 20; i++) {
      const r = new THREE.Mesh(
        new THREE.SphereGeometry(0.3 + Math.random() * 0.5, 6, 6),
        new THREE.MeshLambertMaterial({ color: 0x666666 })
      );
      r.position.set(
        (Math.random() - 0.5) * 50,
        0.3,
        (Math.random() < 0.5 ? -1 : 1) * (12 + Math.random() * 2)
      );
      this.scene.add(r);
    }

    // sky
    const skyGeo = new THREE.SphereGeometry(100, 16, 8);
    const skyMat = new THREE.MeshBasicMaterial({
      color: 0x87ceeb, side: THREE.BackSide,
    });
    this.scene.add(new THREE.Mesh(skyGeo, skyMat));

    // center marker
    const center = new THREE.Mesh(
      new THREE.RingGeometry(2, 2.2, 32),
      new THREE.MeshBasicMaterial({ color: 0xffd84d, transparent: true, opacity: 0.4 })
    );
    center.rotation.x = -Math.PI / 2;
    center.position.y = 0.03;
    this.scene.add(center);
  }

  bindEvents() {
    this.canvas.addEventListener('click', (e) => {
      if (!this.pendingSpell) return;
      const rect = this.canvas.getBoundingClientRect();
      this.mouseNDC.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouseNDC.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      this.raycaster.setFromCamera(this.mouseNDC, this.camera);
      // intersect ground plane y=0
      const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
      const hit = new THREE.Vector3();
      this.raycaster.ray.intersectPlane(ground, hit);
      // find nearest unit to hit
      let target: Unit | null = null;
      let bestD = 4;
      for (const u of this.units) {
        if (u.dead) continue;
        const d = u.pos.distanceTo(hit);
        if (d < bestD) { bestD = d; target = u; }
      }
      if (target) this.castSpell(this.pendingSpell, target, hit);
      this.pendingSpell = null;
      window.dispatchEvent(new CustomEvent('spell-cast'));
    });
  }

  onResize = () => {
    const w = this.canvas.clientWidth || this.canvas.parentElement?.clientWidth || 1280;
    const h = this.canvas.clientHeight || this.canvas.parentElement?.clientHeight || 720;
    if (!w || !h) return;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
  };

  async loadTeams(left: Character[], right: Character[]) {
    // arrange in rows
    const place = (arr: Character[], side: Side) => {
      const n = arr.length;
      const xBase = side === 'left' ? -8 : 8;
      const out: Promise<Unit>[] = [];
      arr.forEach((c, i) => {
        // tighter rows so they fit in the visible field
        const z = (i - (n - 1) / 2) * 2.0;
        const x = xBase + (side === 'left' ? -((i % 2) * 1.3) : ((i % 2) * 1.3));
        out.push(makeUnit(c, side, new THREE.Vector3(x, 0, z)));
      });
      return out;
    };
    const all = await Promise.all([...place(left, 'left'), ...place(right, 'right')]);
    for (const u of all) {
      u.shootProjectile = (target, damage, type, ignoreDef = false) => {
        u.startAttackAnim();
        this.spawnProjectile(u, target, damage, type, ignoreDef);
      };
      u.spawnEffect = (kind) => this.fxByKind(u.pos, kind);
      this.units.push(u);
      this.scene.add(u.group);
    }
    this.leftPool = left.slice();
    this.rightPool = right.slice();
    this.startTime = performance.now();
    this.setupWeather();
    this.pickModifier();
    this.spawnHazards();
    this.applyModifier();
    // Boss intro
    const bosses = this.units.filter(u => u.character.hp >= 200);
    if (bosses.length) {
      window.dispatchEvent(new CustomEvent('battle-event', {
        detail: { kind: 'boss', name: bosses[0].character.name },
      }));
    }
    // Intro fly-by + delayed FIGHT! announce (matches 6s intro)
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('battle-event', { detail: { kind: 'fight' } }));
    }, 5800);
  }

  pickModifier() {
    const opts: typeof this.modifier[] = ['none', 'none', 'meteor_storm', 'double_mana', 'half_hp', 'lava_floor', 'sudden_death'];
    this.modifier = opts[Math.floor(Math.random() * opts.length)];
    if (this.modifier !== 'none') {
      window.dispatchEvent(new CustomEvent('battle-event', { detail: { kind: 'modifier', modifier: this.modifier } }));
    }
  }

  applyModifier() {
    if (this.modifier === 'half_hp') {
      this.units.forEach(u => { u.hp = Math.max(1, u.maxHp * 0.5); });
    } else if (this.modifier === 'sudden_death') {
      this.units.forEach(u => { u.maxHp = u.maxHp * 0.4; u.hp = u.maxHp; });
    } else if (this.modifier === 'lava_floor') {
      // add 5 extra lava patches
      const tex = glowTexture();
      for (let i = 0; i < 5; i++) {
        const x = (Math.random() - 0.5) * (ARENA_X * 1.6);
        const z = (Math.random() - 0.5) * (ARENA_Z * 1.6);
        const geo = new THREE.CircleGeometry(1.8, 24);
        const mat = new THREE.MeshBasicMaterial({ color: 0xff5522, transparent: true, opacity: 0.55, side: THREE.DoubleSide });
        const m = new THREE.Mesh(geo, mat);
        m.rotation.x = -Math.PI / 2;
        m.position.set(x, 0.05, z);
        this.scene.add(m);
        const haloMat = new THREE.SpriteMaterial({ map: tex, color: 0xff5522, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false });
        const halo = new THREE.Sprite(haloMat); halo.scale.set(4, 4, 1); halo.position.set(x, 0.4, z);
        this.scene.add(halo);
        this.hazards.push({ kind: 'lava', pos: new THREE.Vector3(x, 0, z), radius: 1.8, mesh: m });
      }
    }
  }

  setupWeather() {
    const choices: typeof this.weather[] = ['clear', 'clear', 'rain', 'thunderstorm', 'sandstorm'];
    this.weather = choices[Math.floor(Math.random() * choices.length)];
    if (this.weather === 'rain' || this.weather === 'thunderstorm') {
      // build rain particles
      this.rainGroup = new THREE.Group();
      const tex = glowTexture();
      for (let i = 0; i < 220; i++) {
        const mat = new THREE.SpriteMaterial({
          map: tex, color: 0xaaccff, transparent: true, opacity: 0.55,
          blending: THREE.AdditiveBlending, depthWrite: false,
        });
        const sp = new THREE.Sprite(mat);
        sp.scale.set(0.08, 0.6, 1);
        sp.position.set(
          (Math.random() - 0.5) * 60,
          Math.random() * 25,
          (Math.random() - 0.5) * 30
        );
        this.rainGroup.add(sp);
      }
      this.scene.add(this.rainGroup);
      this.scene.background = new THREE.Color(0x556677);
      (this.scene.fog as THREE.Fog).color.setHex(0x556677);
    } else if (this.weather === 'sandstorm') {
      this.scene.background = new THREE.Color(0xc0a060);
      (this.scene.fog as THREE.Fog).color.setHex(0xc0a060);
      (this.scene.fog as THREE.Fog).near = 18;
      (this.scene.fog as THREE.Fog).far = 50;
    }
    window.dispatchEvent(new CustomEvent('battle-event', { detail: { kind: 'weather', weather: this.weather } }));
  }

  spawnHazards() {
    const tex = glowTexture();
    const place = (kind: 'lava' | 'heal' | 'speed', count: number, color: number, radius: number) => {
      for (let i = 0; i < count; i++) {
        const x = (Math.random() - 0.5) * (ARENA_X * 1.4);
        const z = (Math.random() - 0.5) * (ARENA_Z * 1.4);
        const geo = new THREE.CircleGeometry(radius, 24);
        const mat = new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.55, side: THREE.DoubleSide,
        });
        const m = new THREE.Mesh(geo, mat);
        m.rotation.x = -Math.PI / 2;
        m.position.set(x, 0.05, z);
        this.scene.add(m);
        // glow halo
        const haloMat = new THREE.SpriteMaterial({
          map: tex, color, transparent: true, opacity: 0.5,
          blending: THREE.AdditiveBlending, depthWrite: false,
        });
        const halo = new THREE.Sprite(haloMat);
        halo.scale.set(radius * 2.2, radius * 2.2, 1);
        halo.position.set(x, 0.4, z);
        this.scene.add(halo);
        this.hazards.push({ kind, pos: new THREE.Vector3(x, 0, z), radius, mesh: m });
      }
    };
    place('lava', 3, 0xff5522, 1.6);
    place('heal', 2, 0x4dffa8, 1.4);
    place('speed', 1, 0x4dd0ff, 1.4);
  }

  /** Pick a random cinematic camera shot focused on the action. */
  startCinematicShot() {
    const alive = this.units.filter(u => !u.dead);
    if (alive.length < 2) { this.cineCooldown = 4; return; }
    // Find the densest cluster of fighters (the "action") = the unit with the most close neighbors
    let focus = alive[0];
    let bestCount = -1;
    for (const u of alive) {
      let n = 0;
      for (const o of alive) if (o !== u && u.distanceTo(o) < 6) n++;
      if (n > bestCount) { bestCount = n; focus = u; }
    }
    const fp = focus.pos;
    const shots = [
      // 0: Low side dolly past the action
      () => {
        this.cineFrom.set(fp.x - 10, 2.6, fp.z + 4);
        this.cineTo.set(fp.x + 10, 2.6, fp.z + 4);
        this.cineLook.set(fp.x, 2, fp.z);
      },
      // 1: Crash zoom from high to low
      () => {
        this.cineFrom.set(fp.x + 2, 14, fp.z + 14);
        this.cineTo.set(fp.x + 2, 4, fp.z + 6);
        this.cineLook.set(fp.x, 2, fp.z);
      },
      // 2: Orbit arc behind the focus
      () => {
        this.cineFrom.set(fp.x + 8, 5, fp.z + 8);
        this.cineTo.set(fp.x - 8, 5, fp.z + 8);
        this.cineLook.set(fp.x, 2, fp.z);
      },
      // 3: Slow rise straight up
      () => {
        this.cineFrom.set(fp.x + 4, 3, fp.z + 6);
        this.cineTo.set(fp.x + 4, 9, fp.z + 10);
        this.cineLook.set(fp.x, 2, fp.z);
      },
      // 4: Reverse pull-back
      () => {
        this.cineFrom.set(fp.x, 3, fp.z + 4);
        this.cineTo.set(fp.x, 7, fp.z + 16);
        this.cineLook.set(fp.x, 2, fp.z);
      },
    ];
    shots[Math.floor(Math.random() * shots.length)]();
    this.cineDuration = 2.2 + Math.random() * 0.8;
    this.cineT = this.cineDuration;
    this.cineProgress = 0;
    this.cineCooldown = this.cineDuration + 5 + Math.random() * 4;
  }

  spawnReinforcements() {
    const make = async (pool: Character[], side: Side) => {
      if (pool.length === 0) return;
      for (let i = 0; i < 2; i++) {
        const c = pool[Math.floor(Math.random() * pool.length)];
        const x = (side === 'left' ? -10 : 10) + (Math.random() - 0.5) * 2;
        const z = (Math.random() - 0.5) * 6;
        const u = await makeUnit(c, side, new THREE.Vector3(x, 0, z));
        u.shootProjectile = (target, damage, type, ignoreDef = false) => {
          u.startAttackAnim();
          this.spawnProjectile(u, target, damage, type, ignoreDef);
        };
        u.spawnEffect = (kind) => this.fxByKind(u.pos, kind);
        this.units.push(u);
        this.scene.add(u.group);
        // portal burst
        this.spawnBurst(u.pos, side === 'left' ? 0x4dd0ff : 0xff5277, 50);
      }
    };
    make(this.leftPool, 'left');
    make(this.rightPool, 'right');
    window.dispatchEvent(new CustomEvent('battle-event', { detail: { kind: 'reinforcements' } }));
  }

  spawnProjectile(from: Unit, target: Unit, damage: number, type: string, ignoreDef = false) {
    const color = COLORS[type] ?? 0xffffff;
    const group = new THREE.Group();
    const tex = glowTexture();

    // Bright white core (slightly tinted) — small and crisp.
    const coreMat = new THREE.SpriteMaterial({
      map: tex,
      color: 0xffffff,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const core = new THREE.Sprite(coreMat);
    core.scale.set(0.7, 0.7, 1);
    group.add(core);

    // Inner colored glow
    const glowMat = new THREE.SpriteMaterial({
      map: tex, color, transparent: true, opacity: 0.95,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const glow = new THREE.Sprite(glowMat);
    glow.scale.set(1.6, 1.6, 1);
    group.add(glow);

    // Outer wide halo
    const halo2Mat = new THREE.SpriteMaterial({
      map: tex, color, transparent: true, opacity: 0.45,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const halo2 = new THREE.Sprite(halo2Mat);
    halo2.scale.set(3.0, 3.0, 1);
    group.add(halo2);

    group.position.copy(from.pos);
    group.position.y = 1.8;
    this.scene.add(group);

    this.projectiles.push({
      attacker: from,
      pos: group.position,
      target,
      damage,
      type,
      ignoreDef,
      group,
      core: core as any, // typed as Mesh in interface but we use Sprite
      glow,
      speed: 30,
      alive: true,
      trailT: 0,
      spinT: 0,
    });
  }

  shake(time: number, mag: number) {
    if (time > this.shakeT) this.shakeT = time;
    if (mag > this.shakeMag) this.shakeMag = mag;
  }

  /** Map a named effect kind to a distinct visual burst. */
  fxByKind(at: THREE.Vector3, kind: string) {
    const fx: Record<string, [number, number]> = {
      explosion: [0xff6b3d, 60],
      heal:      [0x4dffa8, 30],
      sparkle:   [0xffffff, 40],
      lightning: [0xffee44, 50],
      water:     [0x44aaff, 40],
      fire:      [0xff4422, 50],
      ice:       [0x88ddff, 40],
      grass:     [0x66ee66, 40],
      shadow:    [0x9b59ff, 40],
      psychic:   [0xff66cc, 50],
      dragon:    [0xaa44ff, 60],
      rock:      [0x886644, 40],
      heart:     [0xff88cc, 30],
      dark:      [0x442266, 40],
      moon:      [0xddccff, 40],
      bug:       [0xaadd44, 40],
      sword:     [0xddffff, 40],
      time:      [0x6688ff, 80],
      space:     [0xff44aa, 80],
    };
    const [color, count] = fx[kind] ?? [0xffffff, 30];
    this.spawnBurst(at, color, count);
    // ring of sparks for spectacle
    if (kind === 'time' || kind === 'space' || kind === 'dragon' || kind === 'psychic') {
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        const v = new THREE.Vector3(Math.cos(a) * 6, 2 + Math.random() * 2, Math.sin(a) * 6);
        const mat = new THREE.SpriteMaterial({
          map: sparkTexture(), color, transparent: true, opacity: 1,
          blending: THREE.AdditiveBlending, depthWrite: false,
        });
        const sp = new THREE.Sprite(mat);
        sp.scale.set(0.9, 0.9, 1);
        sp.position.copy(at); sp.position.y += 1;
        this.scene.add(sp);
        this.particles.push({ mesh: sp, vel: v, life: 0.9, maxLife: 0.9, startScale: 0.9, gravity: 4 });
      }
    }
  }

  spawnBurst(at: THREE.Vector3, color: number, count = 20) {
    const tex = sparkTexture();
    for (let i = 0; i < count; i++) {
      const mat = new THREE.SpriteMaterial({
        map: tex, color, transparent: true, opacity: 1,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const sp = new THREE.Sprite(mat);
      const startScale = 0.5 + Math.random() * 0.5;
      sp.scale.set(startScale, startScale, 1);
      sp.position.copy(at);
      sp.position.y += 1.2;
      this.scene.add(sp);
      const v = new THREE.Vector3(
        (Math.random() - 0.5) * 9,
        Math.random() * 6 + 2,
        (Math.random() - 0.5) * 9
      );
      this.particles.push({
        mesh: sp, vel: v,
        life: 0.7 + Math.random() * 0.4,
        maxLife: 1.0,
        startScale,
        gravity: 7,
      });
    }
  }

  /** Soft trail puff (no gravity, slow rise). */
  spawnTrailPuff(at: THREE.Vector3, color: number) {
    const mat = new THREE.SpriteMaterial({
      map: glowTexture(), color, transparent: true, opacity: 0.9,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const sp = new THREE.Sprite(mat);
    const s = 1.2;
    sp.scale.set(s, s, 1);
    sp.position.copy(at);
    this.scene.add(sp);
    this.particles.push({
      mesh: sp, vel: new THREE.Vector3(0, 0.4, 0),
      life: 0.45, maxLife: 0.45, startScale: s, gravity: -0.5,
    });
  }

  spawnFloat(pos: THREE.Vector3, value: number, color = 0xffffff) {
    const c = document.createElement('canvas');
    c.width = 128; c.height = 64;
    const ctx = c.getContext('2d')!;
    ctx.font = 'bold 48px sans-serif';
    ctx.textAlign = 'center';
    ctx.lineWidth = 6;
    ctx.strokeStyle = '#000';
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    ctx.strokeText(String(Math.round(value)), 64, 50);
    ctx.fillText(String(Math.round(value)), 64, 50);
    const tex = new THREE.CanvasTexture(c);
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
    const sp = new THREE.Sprite(mat);
    sp.position.copy(pos);
    sp.position.y += 2.5;
    sp.scale.set(2, 1, 1);
    this.scene.add(sp);
    this.floats.push({ pos: sp.position, value, t: 0, sprite: sp });
  }

  castSpell(kind: string, target: Unit, at: THREE.Vector3) {
    if (target.dead) return;
    // sound effects per spell
    const sfxMap: Record<string, () => void> = {
      fireball: SFX.fireball, freeze: SFX.freeze, heal: SFX.heal,
      lightning: SFX.lightning, meteor: SFX.meteor, shield: SFX.shield, ultimate: SFX.ultimate,
    };
    sfxMap[kind]?.();
    if (kind === 'ultimate') this.ultsCast++;
    if (kind === 'fireball') {
      target.takeDamage(120, 'fire');
      target.applyStatus('burning', 4);
      this.spawnBurst(target.pos, 0xff6b3d, 60);
      this.spawnBurst(target.pos, 0xffee44, 30);
      this.spawnFloat(target.pos, 120, 0xff6b3d);
      this.shake(0.4, 0.5);
    } else if (kind === 'freeze') {
      target.applyStatus('frozen', 5);
      target.applyStatus('stun', 5);
      // big shimmering ice burst
      this.spawnBurst(target.pos, 0x88ddff, 60);
      this.spawnBurst(target.pos, 0xffffff, 30);
      // shard ring
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const v = new THREE.Vector3(Math.cos(a) * 4, 3 + Math.random() * 2, Math.sin(a) * 4);
        const mat = new THREE.SpriteMaterial({
          map: sparkTexture(), color: 0xaaeeff, transparent: true, opacity: 1,
          blending: THREE.AdditiveBlending, depthWrite: false,
        });
        const sp = new THREE.Sprite(mat);
        sp.scale.set(0.8, 0.8, 1);
        sp.position.copy(target.pos); sp.position.y += 1;
        this.scene.add(sp);
        this.particles.push({ mesh: sp, vel: v, life: 0.9, maxLife: 0.9, startScale: 0.8, gravity: 5 });
      }
      this.spawnFloat(target.pos, 0, 0x88ddff);
      this.shake(0.2, 0.25);
    } else if (kind === 'heal') {
      target.heal(target.maxHp * 0.5);
      // rising green sparkles
      for (let i = 0; i < 30; i++) {
        const mat = new THREE.SpriteMaterial({
          map: sparkTexture(), color: 0x4dffa8, transparent: true, opacity: 1,
          blending: THREE.AdditiveBlending, depthWrite: false,
        });
        const sp = new THREE.Sprite(mat);
        sp.scale.set(0.6, 0.6, 1);
        sp.position.copy(target.pos);
        sp.position.x += (Math.random() - 0.5) * 1.6;
        sp.position.z += (Math.random() - 0.5) * 1.6;
        sp.position.y += Math.random() * 0.5;
        this.scene.add(sp);
        this.particles.push({
          mesh: sp,
          vel: new THREE.Vector3((Math.random() - 0.5) * 0.5, 3 + Math.random() * 2, (Math.random() - 0.5) * 0.5),
          life: 1.2, maxLife: 1.2, startScale: 0.6, gravity: -2,
        });
      }
      this.spawnFloat(target.pos, target.maxHp * 0.5, 0x4dffa8);
    } else if (kind === 'lightning') {
      const enemies = this.units.filter(u => !u.dead && u.side !== target.side);
      const hit = [target];
      while (hit.length < 3) {
        const last = hit[hit.length - 1];
        const next = enemies.filter(e => !hit.includes(e)).sort((a,b)=>last.pos.distanceTo(a.pos)-last.pos.distanceTo(b.pos))[0];
        if (!next || last.pos.distanceTo(next.pos) > 8) break;
        hit.push(next);
      }
      // draw chain bolts as line segments
      const pts: THREE.Vector3[] = [new THREE.Vector3(target.pos.x, 14, target.pos.z)];
      hit.forEach(u => {
        const p = u.pos.clone(); p.y = 1.5;
        // jagged segment
        const last = pts[pts.length - 1];
        const steps = 6;
        for (let i = 1; i <= steps; i++) {
          const k = i / steps;
          const px = last.x + (p.x - last.x) * k + (Math.random() - 0.5) * 0.6;
          const py = last.y + (p.y - last.y) * k + (Math.random() - 0.5) * 0.6;
          const pz = last.z + (p.z - last.z) * k + (Math.random() - 0.5) * 0.6;
          pts.push(new THREE.Vector3(px, py, pz));
        }
      });
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineBasicMaterial({ color: 0xffee88, transparent: true, opacity: 1 });
      const line = new THREE.Line(geo, mat);
      this.scene.add(line);
      // fade out via setTimeout
      let fadeT = 0;
      const fade = () => {
        fadeT += 0.05;
        mat.opacity = Math.max(0, 1 - fadeT * 3);
        if (mat.opacity <= 0) { this.scene.remove(line); geo.dispose(); mat.dispose(); }
        else setTimeout(fade, 30);
      };
      setTimeout(fade, 80);
      hit.forEach(u => {
        u.takeDamage(80, 'electric');
        this.spawnBurst(u.pos, 0xffee44, 25);
        this.spawnFloat(u.pos, 80, 0xffee44);
      });
      this.shake(0.3, 0.35);
    } else if (kind === 'meteor') {
      // spawn a falling meteor sphere from the sky
      const radius = 5;
      const mGeo = new THREE.SphereGeometry(1.2, 20, 16);
      const mMat = new THREE.MeshBasicMaterial({ color: 0xff5522 });
      const mesh = new THREE.Mesh(mGeo, mMat);
      mesh.position.set(at.x, 30, at.z);
      this.scene.add(mesh);
      // halo sprite around meteor
      const haloMat = new THREE.SpriteMaterial({
        map: glowTexture(), color: 0xffaa33, transparent: true, opacity: 0.9,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const halo = new THREE.Sprite(haloMat);
      halo.scale.set(4, 4, 1);
      mesh.add(halo);
      const fallMs = 600;
      const startT = performance.now();
      const fall = () => {
        const k = Math.min(1, (performance.now() - startT) / fallMs);
        mesh.position.y = 30 - k * 30 + 0.5;
        // trail
        if (Math.random() < 0.6) this.spawnTrailPuff(mesh.position.clone(), 0xff8833);
        if (k < 1) requestAnimationFrame(fall);
        else {
          this.scene.remove(mesh); mGeo.dispose(); mMat.dispose();
          this.units.filter(u => !u.dead && u.pos.distanceTo(at) < radius).forEach(u => {
            u.takeDamage(150, 'fire');
            u.applyStatus('burning', 3);
            this.spawnFloat(u.pos, 150, 0xff6b3d);
          });
          this.spawnBurst(at, 0xff6b3d, 100);
          this.spawnBurst(at, 0xffee44, 50);
          this.shake(0.6, 0.8);
        }
      };
      fall();
    } else if (kind === 'shield') {
      target.applyStatus('buffed', 8);
      this.spawnBurst(target.pos, 0xffd84d, 40);
    } else if (kind === 'ultimate') {
      // Determine which side it benefits — use targeted unit's side as "ours"
      const friendly = target.side;
      const enemy: Side = friendly === 'left' ? 'right' : 'left';
      // Heal + buff all allies
      this.units.filter(u => !u.dead && u.side === friendly).forEach(a => {
        a.heal(a.maxHp * 0.6);
        a.applyStatus('buffed', 8);
        a.applyStatus('haste', 8);
        this.spawnBurst(a.pos, 0xffd84d, 30);
      });
      // Damage + burn all enemies
      this.units.filter(u => !u.dead && u.side === enemy).forEach(e => {
        e.takeDamage(180, 'fire', false, undefined, false);
        e.applyStatus('burning', 4);
        this.spawnBurst(e.pos, 0xff5522, 50);
        this.spawnFloat(e.pos, 180, 0xff5522);
      });
      this.shake(0.8, 1.0);
      this.ultMeter[friendly] = 0;
      window.dispatchEvent(new CustomEvent('ult-update', { detail: { side: friendly, value: 0, max: this.ULT_MAX } }));
    }
  }

  update(rawDt: number) {
    if (this.paused) return;

    // ----- intro fly-by camera (no game logic during intro) -----
    if (this.introActive) {
      this.introT -= rawDt;
      const k = 1 - Math.max(0, this.introT) / this.introDuration; // 0..1
      const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);
      const easeInOut = (x: number) => x < 0.5 ? 2*x*x : 1 - Math.pow(-2*x+2, 2)/2;
      // 5-stage cinematic sweep over 6 seconds:
      // 0.00–0.18  Wide establishing shot drifting in from above
      // 0.18–0.40  Low slow dolly along Team Left (cyan)
      // 0.40–0.62  Low slow dolly along Team Right (pink)
      // 0.62–0.82  Sweeping arc behind both teams
      // 0.82–1.00  Pull back to gameplay angle
      let cx = 0, cy = 0, cz = 0, lx = 0, ly = 2, lz = 0;
      if (k < 0.18) {
        const t = easeOut(k / 0.18);
        cx = 0; cy = 28 - t * 14; cz = 36 - t * 12;
        lx = 0; ly = 2; lz = 0;
      } else if (k < 0.40) {
        const t = (k - 0.18) / 0.22;
        // dolly past team left, looking sideways at them
        cx = -16 + t * 12;
        cy = 4 + Math.sin(t * Math.PI) * 1.0;
        cz = 8;
        lx = -10 + t * 10;
        ly = 2.2;
        lz = 0;
      } else if (k < 0.62) {
        const t = (k - 0.40) / 0.22;
        // dolly past team right
        cx = 4 + t * 12;
        cy = 4 + Math.sin(t * Math.PI) * 1.0;
        cz = 8;
        lx = 0 + t * 10;
        ly = 2.2;
        lz = 0;
      } else if (k < 0.82) {
        const t = easeInOut((k - 0.62) / 0.20);
        // arc sweep behind, rising
        const angle = -Math.PI * 0.5 + t * Math.PI;
        const r = 22;
        cx = Math.cos(angle) * r * 0.6;
        cy = 7 + t * 4;
        cz = Math.sin(angle) * r;
        lx = 0; ly = 2; lz = 0;
      } else {
        const t = easeInOut((k - 0.82) / 0.18);
        // ease into final base angle
        const sx = 0, sy = 11, sz = 22;
        cx = sx + (this.baseCamPos.x - sx) * t;
        cy = sy + (this.baseCamPos.y - sy) * t;
        cz = sz + (this.baseCamPos.z - sz) * t;
        lx = 0; ly = 2; lz = 0;
      }
      this.camera.position.set(cx, cy, cz);
      this.camera.lookAt(lx, ly, lz);
      // animate units in place even during intro
      for (const u of this.units) u.animate(rawDt);
      this.renderer.render(this.scene, this.camera);
      if (this.introT <= 0) this.introActive = false;
      return;
    }

    // ----- hit-stop on crits -----
    if (this.hitStopT > 0) {
      this.hitStopT -= rawDt;
      // still render but don't tick simulation
      for (const u of this.units) u.animate(rawDt * 0.05);
      return;
    }

    // ----- slow-mo (killing blow) -----
    let dt = rawDt;
    if (this.slowMoT > 0) {
      this.slowMoT -= rawDt;
      dt = rawDt * 0.25;
    }

    // ----- modifier: meteor storm -----
    if (this.modifier === 'meteor_storm') {
      this.meteorStormT -= rawDt;
      if (this.meteorStormT <= 0) {
        this.meteorStormT = 6 + Math.random() * 3;
        const enemies = this.units.filter(u => !u.dead);
        if (enemies.length) {
          const t = enemies[Math.floor(Math.random() * enemies.length)];
          const at = t.pos.clone();
          // mini meteor
          const geo = new THREE.SphereGeometry(0.8, 12, 10);
          const mat = new THREE.MeshBasicMaterial({ color: 0xff5522 });
          const mesh = new THREE.Mesh(geo, mat);
          mesh.position.set(at.x, 25, at.z);
          this.scene.add(mesh);
          const start = performance.now();
          const fall = () => {
            const k = Math.min(1, (performance.now() - start) / 500);
            mesh.position.y = 25 - k * 25;
            if (k < 1) requestAnimationFrame(fall);
            else {
              this.scene.remove(mesh); geo.dispose(); mat.dispose();
              this.units.filter(u => !u.dead && u.pos.distanceTo(at) < 3.5)
                .forEach(u => { u.takeDamage(60, 'fire', false, undefined, false); u.applyStatus('burning', 2); });
              this.spawnBurst(at, 0xff5522, 50);
              this.shake(0.3, 0.4);
              SFX.explosion();
            }
          };
          fall();
        }
      }
    }

    // ----- weather tick -----
    this.weatherT += dt;
    if (this.rainGroup) {
      for (const c of this.rainGroup.children) {
        c.position.y -= dt * 30;
        if (c.position.y < 0) {
          c.position.y = 22 + Math.random() * 5;
          c.position.x = (Math.random() - 0.5) * 60;
          c.position.z = (Math.random() - 0.5) * 30;
        }
      }
    }
    if (this.weather === 'thunderstorm' && Math.random() < dt * 0.4) {
      const alive = this.units.filter(u => !u.dead);
      if (alive.length) {
        const t = alive[Math.floor(Math.random() * alive.length)];
        t.takeDamage(40, 'electric', false, undefined, false);
        this.spawnFloat(t.pos, 40, 0xffee44);
        this.spawnBurst(t.pos, 0xffee44, 30);
        // sky bolt
        const pts = [new THREE.Vector3(t.pos.x, 18, t.pos.z), new THREE.Vector3(t.pos.x, 1, t.pos.z)];
        const geo = new THREE.BufferGeometry().setFromPoints(pts);
        const mat = new THREE.LineBasicMaterial({ color: 0xffee88 });
        const line = new THREE.Line(geo, mat);
        this.scene.add(line);
        setTimeout(() => { this.scene.remove(line); geo.dispose(); mat.dispose(); }, 120);
      }
    }

    // ----- reinforcements at 30s -----
    if (!this.reinforcementsSent && (performance.now() - this.startTime) > 30000) {
      this.reinforcementsSent = true;
      this.spawnReinforcements();
    }

    // ----- hazards: damage / heal / speed pads -----
    for (const u of this.units) {
      if (u.dead) continue;
      for (const h of this.hazards) {
        const dx = u.pos.x - h.pos.x;
        const dz = u.pos.z - h.pos.z;
        if (dx * dx + dz * dz < h.radius * h.radius) {
          if (h.kind === 'lava') {
            u.takeDamage(15 * dt, 'fire', false, undefined, false);
            if (Math.random() < dt * 4) u.applyStatus('burning', 1.5);
          } else if (h.kind === 'heal') {
            u.heal(8 * dt);
          } else if (h.kind === 'speed') {
            u.applyStatus('haste', 0.6);
          }
        }
      }
    }

    // status decay
    for (const u of this.units) {
      if (u.dead) continue;
      for (const k of Object.keys(u.status) as (keyof typeof u.status)[]) {
        u.status[k] = (u.status[k]! - dt);
        if (u.status[k]! <= 0) delete u.status[k];
      }
      u.attackCd = Math.max(0, u.attackCd - dt);
      u.abilityCd = Math.max(0, u.abilityCd - dt);
    }

    // AI
    for (const u of this.units) {
      u.isWalking = false;
      if (u.dead || u.isStunned()) { continue; }
      let target: Unit | null = null;
      let bestD = Infinity;
      for (const o of this.units) {
        if (o.dead || o.side === u.side) continue;
        const d = u.distanceTo(o);
        if (d < bestD) { bestD = d; target = o; }
      }
      if (!target) continue;

      const ability = getAbilityDef(u.character);
      const isRanged = u.character.range_type === 'ranged' || u.character.range_type === 'aoe';
      const desiredRange = isRanged ? Math.min(10, ability.range || 8) : 1.6;

      // ability use
      if (u.abilityCd <= 0 && bestD <= ability.range) {
        u.startAttackAnim();
        ability.cast(u, { units: this.units });
        u.abilityCd = ability.cooldown;
      }

      // movement
      if (bestD > desiredRange) {
        const dir = target.pos.clone().sub(u.pos);
        dir.y = 0;
        if (dir.lengthSq() > 0.0001) dir.normalize();
        const sp = (u.character.speed * 0.04 + 1.8) * u.speedMultiplier();
        u.pos.addScaledVector(dir, sp * dt);
        u.isWalking = true;
      } else {
        // basic auto-attack
        if (u.attackCd <= 0) {
          u.startAttackAnim();
          const dmg = u.character.attack * 0.5;
          if (isRanged) {
            this.spawnProjectile(u, target, dmg, 'physical');
          } else {
            const taken = target.takeDamage(dmg, 'physical', false, u.pos);
            const crit = target.lastHitCrit;
            this.spawnFloat(target.pos, taken, crit ? 0xffdd33 : 0xffffff);
            this.spawnBurst(target.pos, crit ? 0xffdd33 : 0xffffff, crit ? 24 : 8);
            this.shake(crit ? 0.32 : 0.18, crit ? 0.4 : 0.18);
            this.ultMeter[u.side] = Math.min(this.ULT_MAX, this.ultMeter[u.side] + taken);
            window.dispatchEvent(new CustomEvent('ult-update', { detail: { side: u.side, value: this.ultMeter[u.side], max: this.ULT_MAX } }));
            (u as any)._dmg = ((u as any)._dmg ?? 0) + taken;
            if (target.dead) (u as any)._kills = ((u as any)._kills ?? 0) + 1;
            if (crit) { this.hitStopT = Math.max(this.hitStopT, 0.08); this.critsLanded++; SFX.crit(); }
            else SFX.hit();
          }
          const hasteMul = u.status.haste ? 1.4 : 1;
          const lowHp = u.hp / u.maxHp < 0.2 ? 1.5 : 1; // last stand
          u.attackCd = 2.5 / Math.max(0.6, (u.character.speed / 80) * hasteMul * lowHp);
        }
      }

      // Clamp position to arena so units never fly off-canvas.
      if (u.pos.x < -ARENA_X) u.pos.x = -ARENA_X;
      if (u.pos.x > ARENA_X) u.pos.x = ARENA_X;
      if (u.pos.z < -ARENA_Z) u.pos.z = -ARENA_Z;
      if (u.pos.z > ARENA_Z) u.pos.z = ARENA_Z;
    }

    // ----- separation pass: push overlapping units apart -----
    // O(n²) is fine for ~16 units. Keeps melee fighters from stacking
    // on the same tile.
    const MIN_DIST = 2.2;
    for (let i = 0; i < this.units.length; i++) {
      const a = this.units[i];
      if (a.dead) continue;
      for (let j = i + 1; j < this.units.length; j++) {
        const b = this.units[j];
        if (b.dead) continue;
        const dx = b.pos.x - a.pos.x;
        const dz = b.pos.z - a.pos.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < MIN_DIST * MIN_DIST && d2 > 0.0001) {
          const d = Math.sqrt(d2);
          const overlap = (MIN_DIST - d) * 0.5;
          const nx = dx / d;
          const nz = dz / d;
          a.pos.x -= nx * overlap;
          a.pos.z -= nz * overlap;
          b.pos.x += nx * overlap;
          b.pos.z += nz * overlap;
        }
      }
      // re-clamp after push
      if (a.pos.x < -ARENA_X) a.pos.x = -ARENA_X;
      if (a.pos.x > ARENA_X) a.pos.x = ARENA_X;
      if (a.pos.z < -ARENA_Z) a.pos.z = -ARENA_Z;
      if (a.pos.z > ARENA_Z) a.pos.z = ARENA_Z;
    }

    // projectiles
    for (const p of this.projectiles) {
      if (!p.alive) continue;
      if (p.target.dead) { p.alive = false; this.scene.remove(p.group); continue; }
      const targetPos = p.target.pos.clone(); targetPos.y = 1.6;
      const dir = targetPos.clone().sub(p.pos);
      const dist = dir.length();

      // pulse glow
      p.spinT += dt;
      const pulse = 1 + Math.sin(p.spinT * 18) * 0.18;
      p.glow.scale.set(1.6 * pulse, 1.6 * pulse, 1);
      // wobble core for sparkle
      const corePulse = 0.7 + Math.sin(p.spinT * 30) * 0.08;
      p.core.scale.set(corePulse, corePulse, 1);

      if (dist < 0.8) {
        const dmg = p.target.takeDamage(p.damage, p.type, p.ignoreDef, p.attacker.pos);
        const crit = p.target.lastHitCrit;
        this.spawnFloat(p.target.pos, dmg, crit ? 0xffdd33 : (COLORS[p.type] ?? 0xffffff));
        this.spawnBurst(p.target.pos, COLORS[p.type] ?? 0xffffff, crit ? 50 : 28);
        this.spawnBurst(p.target.pos, 0xffffff, crit ? 20 : 8);
        this.shake(crit ? 0.4 : 0.22, crit ? 0.5 : 0.3);
        if (p.attacker) {
          this.ultMeter[p.attacker.side] = Math.min(this.ULT_MAX, this.ultMeter[p.attacker.side] + dmg);
          window.dispatchEvent(new CustomEvent('ult-update', { detail: { side: p.attacker.side, value: this.ultMeter[p.attacker.side], max: this.ULT_MAX } }));
          (p.attacker as any)._dmg = ((p.attacker as any)._dmg ?? 0) + dmg;
          if (p.target.dead) (p.attacker as any)._kills = ((p.attacker as any)._kills ?? 0) + 1;
        }
        if (crit) { this.hitStopT = Math.max(this.hitStopT, 0.08); this.critsLanded++; SFX.crit(); }
        else SFX.hit();
        p.alive = false;
        this.scene.remove(p.group);
      } else {
        dir.normalize();
        p.pos.addScaledVector(dir, p.speed * dt);
        // spawn glowing trail puff
        p.trailT += dt;
        if (p.trailT > 0.018) {
          p.trailT = 0;
          this.spawnTrailPuff(p.pos.clone(), COLORS[p.type] ?? 0xffffff);
        }
      }
    }
    this.projectiles = this.projectiles.filter(p => p.alive);

    // particles
    for (const pt of this.particles) {
      pt.life -= dt;
      pt.mesh.position.addScaledVector(pt.vel, dt);
      pt.vel.y -= pt.gravity * dt;
      // ease scale + opacity for soft fade
      const k = Math.max(0, pt.life / pt.maxLife);
      const ease = k * k; // quadratic falloff
      const m = pt.mesh.material as THREE.SpriteMaterial;
      m.opacity = ease;
      const s = pt.startScale * (0.4 + 0.6 * k);
      pt.mesh.scale.set(s, s, 1);
    }
    this.particles = this.particles.filter(pt => {
      if (pt.life <= 0) { this.scene.remove(pt.mesh); return false; }
      return true;
    });

    // floating numbers
    for (const f of this.floats) {
      f.t += dt;
      f.sprite.position.y += dt * 1.5;
      const m = f.sprite.material as THREE.SpriteMaterial;
      m.opacity = Math.max(0, 1 - f.t / 1.2);
    }
    this.floats = this.floats.filter(f => {
      if (f.t > 1.2) { this.scene.remove(f.sprite); return false; }
      return true;
    });

    // ----- death detection / kill streaks / mana from kills -----
    for (const u of this.units) {
      if (u.dead && !(u as any)._counted) {
        (u as any)._counted = true;
        const killerSide: Side = u.side === 'left' ? 'right' : 'left';
        const ks = this.killStreak[killerSide];
        const now = performance.now() / 1000;
        if (now - ks.lastT < 5) ks.count += 1;
        else ks.count = 1;
        ks.lastT = now;
        // First Blood?
        const isFirstBlood = !this.units.some(o => o !== u && o.dead);
        window.dispatchEvent(new CustomEvent('battle-event', {
          detail: { kind: 'kill', side: killerSide, streak: ks.count, firstBlood: isFirstBlood },
        }));
        // mana for player
        window.dispatchEvent(new CustomEvent('mana-bonus', { detail: { amount: 15 } }));
      }
    }

    // animate units (idle bob, attack lunge, hit flash, knockback, hp bars, death)
    for (const u of this.units) u.animate(dt);

    // ----- 1v1 showdown camera tilt -----
    const aliveLeft = this.units.filter(u => u.side === 'left' && !u.dead);
    const aliveRight = this.units.filter(u => u.side === 'right' && !u.dead);
    const showdown = aliveLeft.length === 1 && aliveRight.length === 1;
    if (showdown && !this.showdownActive) {
      this.showdownActive = true;
      window.dispatchEvent(new CustomEvent('battle-event', { detail: { kind: 'showdown' } }));
    }

    // ----- random cinematic cuts during the fight -----
    this.cineCooldown -= rawDt;
    if (this.cineCooldown <= 0 && this.cineT <= 0 && !showdown && !this.koZoomTarget && !this.winPending) {
      this.startCinematicShot();
    }
    if (this.cineT > 0) this.cineT -= rawDt;

    // camera: KO zoom > showdown tilt > cinematic > shake > base
    let camTarget = this.baseCamPos.clone();
    let lookTarget = new THREE.Vector3(0, 2, 0);
    let lerpRate = 0.15;
    if (this.koZoomTarget) {
      const o = this.koZoomTarget;
      camTarget.set(o.x + 6, 6, o.z + 8);
      lookTarget.set(o.x, 1.5, o.z);
      lerpRate = 0.18;
    } else if (showdown) {
      const mx = (aliveLeft[0].pos.x + aliveRight[0].pos.x) / 2;
      const mz = (aliveLeft[0].pos.z + aliveRight[0].pos.z) / 2;
      camTarget.set(mx, 9, mz + 18);
      lookTarget.set(mx, 2, mz);
      lerpRate = 0.12;
    } else if (this.cineT > 0) {
      // dolly: cineFrom → cineTo over cineDuration
      this.cineProgress += rawDt;
      const t = Math.min(1, this.cineProgress / this.cineDuration);
      const ease = t < 0.5 ? 2*t*t : 1 - Math.pow(-2*t+2, 2)/2;
      camTarget.copy(this.cineFrom).lerp(this.cineTo, ease);
      lookTarget.copy(this.cineLook);
      lerpRate = 0.5; // snap-cut into the move
    }
    if (this.shakeT > 0) {
      this.shakeT -= dt;
      const k = Math.max(0, this.shakeT) * this.shakeMag;
      const t = performance.now() * 0.04;
      camTarget.x += Math.sin(t * 2.7) * k * 0.6;
      camTarget.y += Math.sin(t * 3.3) * k * 0.4;
      camTarget.z += Math.sin(t * 2.1) * k * 0.6;
      this.camera.position.lerp(camTarget, 0.5);
    } else {
      this.camera.position.lerp(camTarget, lerpRate);
    }
    this.camera.lookAt(lookTarget);

    // win condition (delayed via slow-mo killing blow)
    if (!this.winPending) {
      const leftAlive = this.units.some(u => u.side === 'left' && !u.dead);
      const rightAlive = this.units.some(u => u.side === 'right' && !u.dead);
      if (!leftAlive || !rightAlive) {
        const winner: Side = leftAlive ? 'left' : 'right';
        const dur = performance.now() - this.startTime;
        this.winPending = { winner, dur };
        this.winDelay = 1.2;
        this.slowMoT = 1.2;
        this.shake(0.7, 0.7);
        // KO zoom on top killer
        const winners = this.units.filter(u => !u.dead && u.side === winner);
        let best = winners[0];
        for (const u of winners) if (((u as any)._kills ?? 0) > ((best as any)?._kills ?? 0)) best = u;
        if (best) this.koZoomTarget = best.pos;
        SFX[winner === 'left' ? 'victory' : 'defeat']();
      }
    } else {
      this.winDelay -= rawDt;
      if (this.winDelay <= 0) {
        this.paused = true;
        this.onWin?.(this.winPending.winner, this.winPending.dur);
        this.winPending = null;
      }
    }
  }

  start() {
    this.lastT = performance.now();
    const loop = () => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - this.lastT) / 1000);
      this.lastT = now;
      this.update(dt);
      this.renderer.render(this.scene, this.camera);
      this.raf = requestAnimationFrame(loop);
    };
    loop();
  }

  stop() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    this.renderer.dispose();
  }
}
